import { AppLoader } from "../../AppLoader/AppLoader";
import { useAPISetupStatus } from "./useAPISetupStatus";
import { clearApplicationState } from "@app/state/cleanup";
import type { AuthorizationParams } from "@auth0/auth0-react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import type { ComponentType } from "react";
import React, { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

interface AuthenticationGuardProps {
  component: ComponentType;
}

const AuthenticatedComponentWrapper: React.FC<{ component: ComponentType }> = ({ component: Component }) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const isApiSetupComplete = useAPISetupStatus();

  if (isLoading || (isAuthenticated && !isApiSetupComplete)) {
    return <AppLoader />;
  }

  return <Component />;
};

export const AuthenticationGuard: React.FC<AuthenticationGuardProps> = ({ component }) => {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Handle custom login route with invitation parameters
  useEffect(() => {
    if (location.pathname !== "/login" || isLoading) {
      return;
    }

    // this is never true when using cacheLocation="memory" on the Auth0Provider
    // if user is authenticated, redirect to home
    if (isAuthenticated) {
      return void navigate("/");
    }

    // Extract invitation parameters from URL
    const invitation = searchParams.get("invitation");
    const organization = searchParams.get("organization");

    const authorizationParams: AuthorizationParams = {};

    if (invitation && organization) {
      authorizationParams.invitation = invitation;
      authorizationParams.organization = organization;

      // Clear application state when processing invitations to ensure clean state
      // for potentially new user/organization
      void clearApplicationState();
    }

    // Initiate Auth0 login with the parameters if given otherwise do a normal login
    void loginWithRedirect({
      authorizationParams,
      appState: {
        returnTo: "/"
      }
    });
  }, [location.pathname, loginWithRedirect, isAuthenticated, isLoading, searchParams]);

  // If on login route, show loader while processing
  if (location.pathname === "/login") {
    return <AppLoader />;
  }

  const Component = withAuthenticationRequired(AuthenticatedComponentWrapper, {
    onRedirecting: () => <AppLoader />
  });

  return <Component component={component} />;
};
